import { useState } from "react";
import { Send } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { functions } from "../../firebase"; // adjust to your firebase init file
import { resolveIcon } from "@/cms/icons";

const DEFAULT_ITEMS = [
  { icon: "Mail", label: "Email Us", value: "hello@exqura.com", sub: "We reply within 24 hours" },
  { icon: "Phone", label: "Call Us", value: "+65 6123 4567", sub: "Mon – Fri, 9am – 6pm SGT" },
  { icon: "MapPin", label: "Our Office", value: "Singapore", sub: "1 Raffles Place, #20-61" },
  { icon: "Clock", label: "Support Hours", value: "24 / 7", sub: "For urgent booking assistance" },
];

const EMPTY_FORM = { name: "", email: "", subject: "", message: "", website: "" };

/** The contact-details column is CMS-editable (fields.items); the message
 * form on the right is app behavior, not content, so it stays hardcoded
 * with its own local state — same split as ContactUs.jsx had before this
 * page became block-driven. */
export default function ContactDetails({ fields = {} }) {
  const { items = DEFAULT_ITEMS } = fields;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;

    // Honeypot: real users never see or fill this field. Bots do.
    // Pretend success so they get no signal, but send nothing.
    if (form.website) {
      setSent(true);
      return;
    }

    setSending(true);
    setError("");
    try {
      const sendContactMessage = httpsCallable(functions, "sendContactMessage");
      const { name, email, subject, message } = form;
      await sendContactMessage({ name, email, subject, message });
      setSent(true);
    } catch (err) {
      console.error("Contact form submit failed:", err);
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setSent(false);
    setError("");
    setForm(EMPTY_FORM);
  };

  return (
    <section className="pb-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left — contact details */}
        <div className="space-y-6">
          <h2 className="text-white font-bold text-2xl mb-6">{fields.detailsHeading}</h2>
          {items.map((c, i) => {
            const Icon = resolveIcon(c.icon);
            return (
              <div key={i} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-widest mb-0.5">{c.label}</p>
                  <p className="text-white font-semibold text-base">{c.value}</p>
                  <p className="text-white/40 text-sm">{c.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right — form */}
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
          {sent ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center mb-4">
                <Send className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-bold text-2xl mb-2">{fields.successTitle}</h3>
              <p className="text-white/60 text-sm max-w-xs">
                {fields.successText}
              </p>
              <button
                onClick={resetForm}
                className="mt-6 text-[#D4AF37] text-sm font-semibold hover:underline"
              >
                {fields.resetLabel}
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-white font-bold text-2xl mb-6">{fields.formHeading}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot — hidden from users and screen readers */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{fields.nameLabel}</label>
                    <input
                      type="text"
                      required
                      maxLength={100}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={fields.namePlaceholder}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{fields.emailLabel}</label>
                    <input
                      type="email"
                      required
                      maxLength={200}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={fields.emailPlaceholder}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{fields.subjectLabel}</label>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder={fields.subjectPlaceholder}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-widest block mb-1.5">{fields.messageLabel}</label>
                  <textarea
                    required
                    rows={5}
                    maxLength={5000}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={fields.messagePlaceholder}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#D4AF37]/60 transition-colors resize-none"
                  />
                </div>

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#C9A430] disabled:opacity-60 disabled:cursor-not-allowed text-[#0B2421] font-bold rounded-xl px-6 py-3.5 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  {fields.submitLabel}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}